import React, { useEffect } from "react"
import { useTranslation } from "react-i18next"
import { useFormik } from "formik"
import { useSelector } from "react-redux"
import * as yup from "yup"
import { FormControl, InputAdornment, TextField, Button } from "@material-ui/core"
import { makeStyles, useTheme } from "@material-ui/core/styles"
import Simple from "./Simple"

const useStyles = makeStyles((theme) => ({
	root: {
		display: "flex",
		flexWrap: "wrap",
	},
	margin: {
		margin: theme.spacing(2),
	},

	formActions: {
		display: "flex",
		justifyContent: "flex-end",
		flexDirection: "row",
		width: "100%",
		gap: "4px",
	},
	formActionsBtn: {
		"&:disabled": {
			backgroundColor: theme.palette.grey[300] + " !important",
		},
		textTransform: "none",
	},
	textField: {
		width: "327px",
		"& .MuiOutlinedInput-input": {
			paddingLeft: "12px",
			...theme.typography.body2,
		},
		"& fieldset > legend": {
			float: "none",
		},
		"& input[type='text']:focus": {
			boxShadow: "none",
		},
	},
	inputFormControl: {
		padding: "12px 0 24px",
	},
	bulkDownloadDialog: {
		"& .MuiDialogTitle-root > h2": {
			...theme.typography.h6,
			fontWeight: theme.typography.fontWeightMedium,
			wordWrap: "break-word",
		},
	},
	formActionsCancelBtn: {
		color: theme.palette.grey["D100"],
		fontSize: theme.typography.fontSize,
		fontWeight: theme.typography.fontWeightMedium,
		textTransform: "none",
		"&:hover": {
			background: "none",
		},
	},
}))

function DownloadAs(props) {
	const theme = useTheme()
	const classes = useStyles(theme)
	const [t] = useTranslation("global")

	const { bulk_download } = useSelector((state) => state?.downloads)

	const fileNameRgx = /[&\/\#,+()$~%.'":@^*?<>{}]/gi

	const handleSubmit = (data) => {
		const fileName = data?.file_name?.trim()

		if (fileName) {
			const filteredFileName = fileName?.replace(fileNameRgx, "-")
			props?.handleBulkDownload(filteredFileName)
		}
	}

	const downloadValidationSchema = yup.object({
		file_name: yup.string().trim().max(30, t("downloadDialog.errorMsg.fileNameLimit")).required(t("downloadDialog.errorMsg.required")),
	})

	const downloadAsForm = useFormik({
		initialValues: {
			file_name: t("downloadDialog.defaultFileName"),
		},
		validationSchema: downloadValidationSchema,
		onSubmit: handleSubmit,
	})

	const filteredProps = {
		...props,
		onClose: null,
		dynamicClass: classes.bulkDownloadDialog,
	}

	useEffect(() => {
		if (bulk_download?.downloading) {
			props?.onClose()
			downloadAsForm?.resetForm()
		}
	}, [bulk_download])

	return (
		<Simple {...filteredProps}>
			<form noValidate autoComplete="off" id="download-documents" onSubmit={downloadAsForm?.handleSubmit}>
				<FormControl className={classes.inputFormControl}>
					<TextField
						id="file_name"
						label={t("downloadDialog.textFieldLabel")}
						InputProps={{
							endAdornment: <InputAdornment position="end">{t("downloadDialog.fileExtension")}</InputAdornment>,
						}}
						variant="outlined"
						className={classes.textField}
						value={downloadAsForm.values.file_name}
						onChange={downloadAsForm.handleChange}
						onBlur={downloadAsForm.handleBlur}
						error={downloadAsForm.touched.file_name && Boolean(downloadAsForm.errors.file_name)}
						helperText={downloadAsForm.touched.file_name && downloadAsForm.errors.file_name}
					/>
				</FormControl>
				<FormControl className={classes.formActions}>
					<Button
						variant="contained"
						color="primary"
						className={classes.formActionsBtn}
						style={{
							padding: "4px 12px",
							background: theme.palette.primary.dark,
							fontWeight: theme.typography.fontWeightMedium,
						}}
						type="submit"
						disabled={!downloadAsForm.isValid}
					>
						{t("downloadDialog.downloadBtn")}
					</Button>
					<Button
						variant="text"
						className={classes.formActionsCancelBtn}
						onClick={() => {
							downloadAsForm?.resetForm()
							props?.onClose()
						}}
					>
						{t("downloadDialog.cancelBtn")}
					</Button>
				</FormControl>
			</form>
		</Simple>
	)
}

export default DownloadAs
